import { useQuery } from "@tanstack/react-query"
import { BadgeCheckIcon } from "lucide-react"
import { ReactNode } from "react"
import {
  DocumentStatusMap,
  getDocument,
  getSignee,
  SignatureServiceMap,
} from "@/api/api"
import { EcertGuide } from "@/components/ecert-guide"
import { NextStep } from "@/components/next-step"
import { PageLoader } from "@/components/page-loader"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card"
import Icon, { IconName } from "@/components/ui/icon"
import { Separator } from "@/components/ui/separator"
import { useCurrentToken } from "@/lib/use-current-token"

const Explanation = ({
  title,
  icon,
  children,
}: {
  title: string
  icon: IconName
  children: ReactNode
}) => {
  return (
    <div>
      <div className="mb-2 flex flex-row items-center justify-start gap-2 md:text-lg">
        <Icon name={icon} />
        <h2 className="font-bold">{title}</h2>
      </div>

      <div className="md:pl-8">{children}</div>
    </div>
  )
}

const StartPage = () => {
  const { token_id, signee_id, document_id, isReady } = useCurrentToken()

  const query = useQuery({
    queryKey: ["getDocument", { document_id, token_id }],
    queryFn: () => getDocument({ document_id: document_id! }),
    enabled: isReady && !!document_id,
  })

  const signeeQuery = useQuery({
    queryKey: ["getSignee", { document_id, signee_id, token_id }],
    queryFn: () =>
      getSignee({ document_id: document_id!, signee_id: signee_id! }),
    enabled: isReady && !!document_id && !!signee_id,
  })

  if (!isReady || query.isLoading || signeeQuery.isLoading) {
    return <PageLoader />
  }

  if (query.isError) {
    throw query.error
  }

  if (!query.isSuccess || !query.data) {
    throw new Error("Unexpected error")
  }

  const currentDocument = query.data
  const currentSignee = signeeQuery.data

  if (currentDocument.status == DocumentStatusMap.SIGNED) {
    return (
      <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
        <Card className="shadow-lg">
          <CardContent className="flex flex-col items-center justify-center gap-4 pt-6">
            <div>
              <BadgeCheckIcon size={92} className="text-primary" />
            </div>
            <div>Documento ya fue firmado</div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
      <Card className="shadow-lg">
        <CardHeader className="">
          <h1 className="text-center text-2xl">
            Solicitud de firma de documento
          </h1>

          <CardDescription className="text-center">
            Has sido invitado a firmar el documento{" "}
            <strong>{currentDocument.name}</strong>. Este es un proceso{" "}
            <strong>100% en línea</strong>, no necesitas acudir presencialmente
            a una notaría, ahorrándote horas de traslado y espera. Para
            continuar lee cuidadosamente las indicaciones que se muestran a
            continuación.
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-6">
          {currentSignee?.verification.includes("email") && (
            <Explanation title="Verificación de correo electrónico" icon="mail">
              Se enviará un código de verificación a tu correo electrónico.
              Debes revisar tu bandeja de entrada y, si no lo encuentras,
              verifica en tu carpeta de spam o correo no deseado.
            </Explanation>
          )}

          {currentSignee?.verification.includes("card_id") && (
            <Explanation title="Documento de identidad" icon="square-user">
              Debes tener tu cédula de identidad a mano. Asegúrate de que el
              documento esté vigente y en buenas condiciones.
            </Explanation>
          )}

          {currentSignee?.verification.includes("video") && (
            <Explanation title="Verificación por video" icon="video">
              Busca un lugar bien iluminado donde puedas grabar un video. La
              buena iluminación es crucial para que tu rostro sea claramente
              visible. Deberás seguir las instrucciones indicadas para confirmar
              tu identidad de manera segura.
            </Explanation>
          )}

          {currentDocument.service == SignatureServiceMap.ECERT && (
            <Explanation title="Firma Electrónica Avanzada" icon="signature">
              <p className="mb-4">
                Una vez que hayas completado los pasos anteriores, podrás firmar
                el documento digitalmente desde tu dispositivo. Todo el proceso
                es en línea, sin necesidad de visitar una notaría.
              </p>
              <EcertGuide />
            </Explanation>
          )}

          {currentDocument.service == SignatureServiceMap.FIRME_SIMPLE && (
            <Explanation title="Firma Electrónica Simple" icon="signature">
              A continuación, podrás visualizar el documento y firmarlo
              agregando tu firma a mano alzada desde tu dispositivo. Todo el
              proceso es en línea, sin necesidad de visitar una notaría.
              Asegúrate de seguir todas las indicaciones para que tu firma
              digital sea válida y segura.
            </Explanation>
          )}
        </CardContent>

        <Separator className="mb-6" />

        <CardFooter className="flex md:justify-end">
          <NextStep />
        </CardFooter>
      </Card>
    </div>
  )
}

export { StartPage }
